import React, { useEffect, useMemo, useState } from "react";
import { C } from "../ipd-billing/constants";
import { fetchDiagnosticReview } from "./diagnosticReportApi";

const EMPTY_REJECTION = { rejected: false, reason: "" };

export function useDiagnosticReview({
  patientLabTestId,
  extraIds = [],
  roleType,
} = {}) {
  const [signatories, setSignatories] = useState([]);
  const [rejection, setRejection] = useState(EMPTY_REJECTION);
  const [publishedByPltId, setPublishedByPltId] = useState({});
  const idsKey = useMemo(() => {
    const ids = [patientLabTestId, ...extraIds]
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0);
    return [...new Set(ids)].join(",");
  }, [patientLabTestId, extraIds]);

  useEffect(() => {
    let cancelled = false;
    const ids = idsKey
      ? idsKey.split(",").map(Number).filter((n) => n > 0)
      : [];
    if (ids.length === 0) {
      setSignatories([]);
      setRejection(EMPTY_REJECTION);
      setPublishedByPltId({});
      return undefined;
    }
    Promise.all(
      ids.map((id) => fetchDiagnosticReview(id, { roleType }))
    ).then((reviews) => {
      if (cancelled) return;
      const byKey = new Map();
      reviews.forEach((r) => {
        (r.signatories || []).forEach((s) => {
          const key = s.id != null ? `id:${s.id}` : `name:${s.name}`;
          const prev = byKey.get(key);
          if (!prev || (!prev.image && s.image)) byKey.set(key, s);
        });
      });
      const rejected = reviews.find((r) => r.rejection?.rejected);
      const publishedMap = {};
      ids.forEach((id, index) => {
        if (reviews[index]?.published) publishedMap[id] = true;
      });
      setSignatories([...byKey.values()]);
      setRejection(rejected?.rejection || EMPTY_REJECTION);
      setPublishedByPltId(publishedMap);
    });
    return () => {
      cancelled = true;
    };
  }, [idsKey, roleType]);

  return { signatories, rejection, publishedByPltId };
}

export function useDiagnosticSignatories(args) {
  return useDiagnosticReview(args).signatories;
}

/** On-screen signed pathologist / radiologist block for every report viewer. */
export default function DiagnosticSignatories({ signatories }) {
  if (!signatories?.length) return null;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 28,
        flexWrap: "wrap",
        marginTop: 18,
        paddingTop: 12,
        borderTop: `1px solid ${C.border}`,
      }}
    >
      {signatories.map((s) => (
        <div
          key={String(s.id || s.name)}
          style={{ textAlign: "center", minWidth: 130 }}
        >
          {s.image ? (
            <img
              src={s.image}
              alt=""
              style={{
                height: 48,
                maxWidth: 150,
                objectFit: "contain",
                display: "block",
                margin: "0 auto 6px",
              }}
            />
          ) : (
            <div
              style={{
                height: 48,
                borderBottom: `1px solid ${C.text}`,
                marginBottom: 6,
              }}
            />
          )}
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
            {s.name || "Signed"}
          </div>
          <div style={{ fontSize: 10, color: C.textSub, marginTop: 2 }}>
            {s.roleLabel || "Consultant"}
          </div>
        </div>
      ))}
    </div>
  );
}
